import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { ZhhRealityCheckService as ZhhRealityCheckServiceInterface } from "@obg/core.injection";
import { AlertButtonStyle } from "@obg/mat.ui.core";
import { CommonState, ZhhSessionLimitAction, ZhhSessionLimitSelector } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CORE_ROUTE,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { RouteAction } from "@obg/ngx.router";
import { combineLatest, Subscription, timer } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { ZhhRealityCheckAlertContainer } from "../reality-check-alert/zhh-reality-check-alert.container";

import {
	ZHH_REALITY_CHECK_ALERT_ID,
	ZHH_REALITY_CHECK_ALERT_TITLE_FINAL,
	ZHH_REALITY_CHECK_ALERT_TITLE_REMINDER,
} from "./zhh-reality-check.const";

@Injectable({
	providedIn: "root",
})
export class ZhhRealityCheckService implements ZhhRealityCheckServiceInterface {
	private timer$$: Subscription | undefined;

	private msTwoMinutes = 2 * 60 * 1000;
	private msTenMinutes = 10 * 60 * 1000;

	constructor(
		private store: Store<CommonState>,
		private zhhSessionLimitSelector: ZhhSessionLimitSelector,
		private zhhSessionLimitAction: ZhhSessionLimitAction,
		private alertService: AbstractTranslatableAlertService
	) {}

	init() {
		combineLatest([
			this.store.pipe(AuthSelector.isAuthenticated$),
			this.store.pipe(select(JurisdictionSelector.get)),
			this.store.pipe(select(this.zhhSessionLimitSelector.getZhhSessionLimit())),
		])
			.pipe(
				filter(([, , state]) => !!state?.sessionLimit),
				tap(([isAuthenticated, jurisdiction, sessionState]) => {
					if (!isAuthenticated || jurisdiction !== Jurisdiction.Zhh || !sessionState?.sessionLimit) {
						return;
					}

					this.timer$$?.unsubscribe();

					if (sessionState.sessionLimit.blocked) {
						this.openRealityCheckDialog(ZHH_REALITY_CHECK_ALERT_TITLE_FINAL);
						return;
					}

					let timerEndTime = sessionState?.sessionLimit?.sessionEndTime
						? Date.parse(sessionState.sessionLimit.sessionEndTime)
						: Date.now() + this.msTenMinutes;

					if (timerEndTime - Date.now() < 0) {
						timerEndTime = Date.now() + this.msTenMinutes;
					}

					if (timerEndTime - Date.now() > this.msTwoMinutes) {
						this.startTimer(timerEndTime - this.msTwoMinutes, true);
						return;
					}

					this.startTimer(timerEndTime, false);
				})
			)
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	startTimer(endTime: number, reminder: boolean) {
		const msDiff = endTime - Date.now() + 1000;
		this.timer$$ = timer(msDiff).subscribe(() => {
			if (reminder) {
				this.openRealityCheckDialog(ZHH_REALITY_CHECK_ALERT_TITLE_REMINDER);
			}
			this.store.dispatch(this.zhhSessionLimitAction.get());
		});
	}

	openRealityCheckDialog(type: string) {
		this.alertService.open(
			{
				id: ZHH_REALITY_CHECK_ALERT_ID,
				hideClose: false,
				messageComponent: ZhhRealityCheckAlertContainer,
				messageComponentInit: (component: ZhhRealityCheckAlertContainer) =>
					(component.data = {
						title: `reality-check.title-${type}`,
						dialogContent: `reality-check.content-${type}`,
						dialogContentSubText: `reality-check.content-sub-text-${type}`,
					}),
				dialogConfig: {
					closeOnNavigation: false,
					panelClass: "zhh-reality-check-alert",
				},
				buttons: [
					{
						text: "reality-check.btn-redirect",
						style: AlertButtonStyle.Flat,
						handler: () => this.store.dispatch(RouteAction.navigate(CORE_ROUTE.lobbies.sportsbook)),
					},
				],
			},
			[]
		);
	}

	close(): void {
		this.timer$$?.unsubscribe();
	}
}
